import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { PublicStats as Stats } from '../../../../shared/schemas';
import { landing } from '../../content/landing';
import { Reveal } from './Reveal';

/**
 * Dải số liệu cộng dồn của mọi server — bằng chứng cho khối khác biệt phía trên.
 *
 * Số đến từ `/api/public/stats`, tức từ nhật ký bot đang chạy, không từ một
 * bản chép tay trong repo. Đó là cả điểm của khối này: một trang bán hàng tự
 * khai con số của mình thì con số đó không chứng minh được gì.
 *
 * ── Ẩn hẳn thay vì hiện số 0 ───────────────────────────────────────────────
 *
 * Ba trường hợp đều ra cùng một kết quả là **không vẽ gì cả**: đang tải, gọi
 * lỗi, và máy chủ trả `null` (chưa đủ điều kiện công bố — xem
 * `lib/publicStats.ts`). Không có khung xám chờ, không có "đang thu thập số
 * liệu", không có ô 0.
 *
 * Lý do: đây là khối DUY NHẤT trên trang mà sự vắng mặt của nó không để lại lỗ
 * hổng nào trong lập luận — phần trên vẫn đứng vững một mình. Còn một ô ghi số
 * 0, hay một khung xương chờ mãi không có số, thì nói với người đọc đúng cái
 * điều mình không muốn nói.
 */
async function fetchStats(): Promise<Stats | null> {
  // `fetch` trần, không phải `apiFetch`: đây là trang công khai cho người chưa
  // có phiên nào — cùng lý do đã ghi ở `pages/public/Commands.tsx`.
  const response = await fetch('/api/public/stats');
  if (!response.ok) throw new Error(`HTTP ${String(response.status)}`);
  const body = (await response.json()) as { stats?: Stats | null };
  return body.stats ?? null;
}

const numberFormat = new Intl.NumberFormat('en-US');

/** `1430` → `1,430`. */
function count(value: number): string {
  return numberFormat.format(value);
}

/** `1600` → `1.6s`. Trên một phút thì giây mất nghĩa, chuyển sang phút. */
function duration(ms: number): string {
  if (ms >= 60_000) return `${(ms / 60_000).toFixed(1)}m`;
  return `${(ms / 1000).toFixed(1)}s`;
}

/** `2026-06-15` → `June 2026`. */
function monthYear(day: string): string {
  const parsed = new Date(`${day}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return day;
  return parsed.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

/** Điền `{tên}` trong chuỗi nội dung. Chỗ điền thiếu thì giữ nguyên để lộ ra ngay. */
function fill(template: string, values: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (whole, key: string) => values[key] ?? whole);
}

/**
 * Số đếm chạy từ 0 tới giá trị thật khi ô số cuộn vào màn hình — chạy MỘT lần,
 * ease-out để đoạn cuối chậm lại đúng chỗ mắt người bắt đầu đọc số.
 *
 * `format` nhận số trung gian nên dấu phẩy nghìn/đơn vị giây giữ đúng suốt lúc
 * chạy. Người tắt animation thấy ngay giá trị cuối, không thấy số 0 nhấp nháy.
 */
function CountUp({
  value,
  format,
}: {
  value: number;
  format: (n: number) => string;
}): React.JSX.Element {
  const ref = useRef<HTMLSpanElement | null>(null);
  const [shown, setShown] = useState(0);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setShown(value);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting !== true || started.current) return;
        started.current = true;
        io.disconnect();
        const begin = performance.now();
        const step = (now: number): void => {
          const t = Math.min(1, (now - begin) / 1_100);
          const eased = 1 - (1 - t) ** 3;
          setShown(Math.round(value * eased));
          if (t < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      },
      { threshold: 0.4 },
    );
    io.observe(el);
    return () => { io.disconnect(); };
  }, [value]);

  return <span ref={ref}>{format(shown)}</span>;
}

export function PublicStats(): React.JSX.Element | null {
  const { data } = useQuery({
    queryKey: ['public', 'stats'],
    queryFn: fetchStats,
    // Máy chủ đã đệm một giờ; hỏi lại sớm hơn thế chỉ tốn lượt gọi mà nhận đúng
    // con số cũ.
    staleTime: 30 * 60_000,
    retry: false,
  });

  // Không có số, hoặc chưa có gì để khoe → không vẽ gì. Xem chú thích đầu file.
  if (!data) return null;
  if (data.pairs.total === 0 && data.questionsLogged === 0) return null;

  const { eyebrow, caption, tiles, languages } = landing.stats;

  // `value` giữ dạng SỐ (kèm hàm định dạng riêng) thay vì chuỗi dựng sẵn, vì
  // `CountUp` phải định dạng lại từng giá trị trung gian trong lúc đếm.
  const cells = [
    {
      value: data.pairs.total,
      format: count,
      label: tiles.pairs.label,
      note: fill(tiles.pairs.note, {
        live: count(data.pairs.live),
        accepted: count(data.pairs.accepted),
        ticket: count(data.pairs.ticket),
      }),
    },
    {
      value: data.questionsLogged,
      format: count,
      label: tiles.questions.label,
      note: tiles.questions.note,
    },
    {
      value: data.yielded,
      format: count,
      label: tiles.yielded.label,
      note: fill(tiles.yielded.note, { answered: count(data.answered) }),
    },
    // Trung vị vắng mặt khi chưa lượt trả lời nào ghi được thời gian — bỏ ô đó
    // đi thay vì hiện một gạch ngang.
    ...(data.medianMs === null
      ? []
      : [
          {
            value: data.medianMs,
            format: duration,
            label: tiles.latency.label,
            note: tiles.latency.note,
          },
        ]),
  ];

  return (
    <Reveal>
    <div className="rounded-2xl border border-border bg-surface-2 p-5 sm:p-8">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        {/* Mono cùng lý do với eyebrow của khối khác biệt — xem `Differentiators.tsx`. */}
        <p className="font-mono text-xs font-semibold tracking-widest text-accent uppercase">{eyebrow}</p>
        {data.since !== null && (
          <p className="text-xs text-fg-subtle">
            {fill(caption, { since: monthYear(data.since) })}
          </p>
        )}
      </div>

      <dl className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {cells.map((cell) => (
          <div
            key={cell.label}
            className="rounded-xl border border-border bg-surface p-5 shadow-sm"
          >
            <dd className="font-mono text-3xl font-bold tracking-tight tabular-nums text-accent">
              <CountUp value={cell.value} format={cell.format} />
            </dd>
            <dt className="mt-1 text-sm font-semibold">{cell.label}</dt>
            <p className="mt-1.5 text-xs leading-relaxed text-fg-subtle">{cell.note}</p>
          </div>
        ))}
      </dl>

      {data.languages.length > 0 && (
        <p className="mt-4 text-xs text-fg-muted">
          <span className="font-semibold">{languages.label}</span>{' '}
          {data.languages
            .map((entry) => `${languages.names[entry.code] ?? entry.code} ${count(entry.count)}`)
            .join(' · ')}
        </p>
      )}
    </div>
    </Reveal>
  );
}
